import { CommonModule } from '@angular/common';
import { Component, HostListener, Inject, computed, signal } from '@angular/core';

export interface TimePickerModalData {
  initial?: string | null;
}

@Component({
  selector: 'app-time-picker-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './time-picker-modal.component.html',
  styleUrls: ['./time-picker-modal.component.scss'],
})
export class TimePickerModalComponent {
  public readonly hours: number[] = Array.from({ length: 11 }, (_, i) => i);
  public readonly minutes: number[] = [0, 15, 30, 45];

  public readonly selectedHour = signal(1);
  public readonly selectedMinute = signal(0);

  public readonly preview = computed(() => this.format(this.selectedHour(), this.selectedMinute()));

  // 0:00h is not a valid duration, so minute 00 is off-limits while hour 0 is selected.
  public isMinuteDisabled(minute: number): boolean {
    return minute === 0 && this.selectedHour() === 0;
  }

  constructor(
    @Inject('MODAL_DATA') private data: TimePickerModalData,
    @Inject('MODAL_CLOSE') private modalClose: (result: string | null) => void
  ) {
    const parsed = this.parse(this.data?.initial);
    if (parsed) {
      this.selectedHour.set(parsed.hour);
      this.selectedMinute.set(parsed.minute);
    }
  }

  public selectHour(hour: number): void {
    this.selectedHour.set(hour);
    // Hour 0 can't be combined with minute 00 (a 0:00h duration is meaningless) — bump
    // the minute forward to the next valid option instead of leaving an invalid state.
    if (hour === 0 && this.selectedMinute() === 0) {
      this.selectedMinute.set(15);
    }
  }

  public selectMinute(minute: number): void {
    if (this.isMinuteDisabled(minute)) return;
    this.selectedMinute.set(minute);
  }

  public close(): void {
    this.modalClose(null);
  }

  @HostListener('document:keydown.escape', ['$event'])
  public handleEscape(event: Event): void {
    event.preventDefault();
    this.close();
  }

  public confirm(): void {
    // Defensive: the 00-minute button is disabled while hour is 0, so this should be unreachable.
    if (this.selectedHour() === 0 && this.selectedMinute() === 0) return;
    this.modalClose(this.preview());
  }

  private format(hour: number, minute: number): string {
    return `${hour}:${minute.toString().padStart(2, '0')}h`;
  }

  private parse(value?: string | null): { hour: number; minute: number } | null {
    if (!value) return null;
    const match = /^(\d+):(\d{2})h$/.exec(value.trim());
    if (!match) return null;
    return { hour: parseInt(match[1], 10), minute: parseInt(match[2], 10) };
  }
}
